import { useState, useEffect } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  X,
} from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

type ToastListener = (toasts: ToastMessage[]) => void;

class ToastService {
  private toasts: ToastMessage[] = [];
  private listeners: Set<ToastListener> = new Set();

  public subscribe(listener: ToastListener) {
    this.listeners.add(listener);
    listener(this.toasts);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  public show(message: string, type: ToastType = "info", duration = 4000) {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: ToastMessage = { id, type, message, duration };
    this.toasts.push(newToast);
    this.notify();

    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }
  }

  public success(message: string, duration?: number) {
    this.show(message, "success", duration);
  }

  public error(message: string, duration?: number) {
    this.show(message, "error", duration);
  }

  public info(message: string, duration?: number) {
    this.show(message, "info", duration);
  }

  public warning(message: string, duration?: number) {
    this.show(message, "warning", duration);
  }

  public remove(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }
}

export const toast = new ToastService();
export const toastService = toast;

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    return toast.subscribe((currentToasts) => {
      setToasts(currentToasts);
    });
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map((item) => {
        const isSuccess = item.type === "success";
        const isError = item.type === "error";
        const isWarning = item.type === "warning";

        const bg = isSuccess
          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : isError
            ? "bg-rose-50 border-rose-300 text-rose-800"
            : isWarning
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-violet-50 border-violet-300 text-violet-800";

        const Icon = isSuccess
          ? CheckCircle2
          : isError
            ? AlertCircle
            : isWarning
              ? AlertTriangle
              : Info;

        const iconColor = isSuccess
          ? "text-emerald-400"
          : isError
            ? "text-rose-400"
            : isWarning
              ? "text-amber-400"
              : "text-purple-400";

        return (
          <div
            key={item.id}
            className={`pointer-events-auto flex items-center justify-between p-4 rounded-xl border backdrop-blur-md shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-4 ${bg}`}
          >
            <div className="flex items-center gap-3 pr-2">
              <Icon className={`h-5 w-5 shrink-0 ${iconColor}`} />
              <span className="font-semibold leading-snug">{item.message}</span>
            </div>
            <button
              onClick={() => toast.remove(item.id)}
              className="text-slate-400 hover:text-white transition-colors p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
